import { useSyncExternalStore } from "react"

import { CACHED_AT_HEADER } from "./cache-policy"

/**
 * ПОДКЛЮЧЕНИЕ ОФЛАЙН-ЧТЕНИЯ И ОТМЕТКА ВОЗРАСТА ДАННЫХ (П1-12).
 *
 * Здесь три вещи, и все три нужны человеку, а не браузеру:
 *
 * 1. РЕГИСТРАЦИЯ работника. Только в собранном приложении: в разработке service worker
 *    перехватывал бы модули Vite и ломал горячую перезагрузку — то есть мешал бы там,
 *    где офлайн никому не нужен.
 * 2. ОТМЕТКА «ЭТО ИЗ КЕША». Ответ, отданный без связи, несёт заголовок с моментом
 *    сохранения. Экран обязан его показать: «Показываю расписание, загруженное сегодня
 *    в 09:15» — единственное, что отделяет рабочий экран от вранья (ТЗ 05 §5.14).
 * 3. СТИРАНИЕ при выходе. В кеше лежат имена и телефоны клиентов; «Выйти» на телефоне,
 *    который отдали ребёнку, обязано означать, что вчерашний день салона больше
 *    не прочитать.
 */

/**
 * Момент сохранения последнего ответа ПО АДРЕСУ.
 *
 * Ключ — путь без строки запроса: журнал спрашивают с датами и фильтрами, и экрану нужно
 * «когда я в последний раз получил расписание», а не «когда именно эту комбинацию
 * параметров». Хранится в памяти вкладки: после перезагрузки первый же ответ поставит
 * отметку заново, а придумывать её самим нельзя — соврём про возраст данных.
 */
const cachedAt = new Map<string, string>()
const listeners = new Set<() => void>()
let version = 0

function emit(): void {
  version += 1
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function pathKey(path: string): string {
  const cut = path.indexOf("?")
  return cut === -1 ? path : path.slice(0, cut)
}

/**
 * Запомнить, откуда пришёл ответ. Зовётся единственной дверью наружу (api/http.ts).
 *
 * `at === null` означает «пришло из сети»: отметка СНИМАЕТСЯ. Без этого журнал, один раз
 * прочитанный из кеша, до конца сеанса писал бы «показываю сохранённое» — и человек
 * перестал бы верить полосе состояния, когда связь уже вернулась.
 */
export function noteResponseSource(path: string, at: string | null): void {
  const key = pathKey(path)
  const previous = cachedAt.get(key) ?? null
  if (previous === at) return
  if (at === null) cachedAt.delete(key)
  else cachedAt.set(key, at)
  emit()
}

/** Прочитать заголовок из ответа: вызывается там же, где ответ разбирается. */
export function readCachedAt(headers: Headers): string | null {
  return headers.get(CACHED_AT_HEADER)
}

/** Когда сохранён последний ответ по этому адресу. null — ответ пришёл из сети. */
export function cachedAtOf(path: string): string | null {
  return cachedAt.get(pathKey(path)) ?? null
}

/** То же для экрана: перерисовывается, когда источник ответа меняется. */
export function useCachedAt(path: string): string | null {
  const key = pathKey(path)
  return useSyncExternalStore(
    subscribe,
    () => cachedAt.get(key) ?? null,
    () => null,
  )
}

/** Версия отметок — для тестов и отладки: по ней видно, что уведомление дошло. */
export function cachedAtVersion(): number {
  return version
}

function workerAvailable(): boolean {
  return typeof navigator !== "undefined" && "serviceWorker" in navigator
}

/**
 * Подключить офлайн-чтение.
 *
 * Молча ничего не делает там, где работник недоступен: http (кроме localhost), приватное
 * окно, отключённый пользователем механизм. Кабинет от этого не ломается — он просто
 * работает как раньше, только со связью.
 */
export function registerOfflineCache(): void {
  if (!workerAvailable()) return
  if (!import.meta.env.PROD) return
  // Регистрация после загрузки страницы: работник не должен спорить за сеть с первыми
  // запросами, от которых зависит первый экран.
  const start = (): void => {
    void navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch(() => undefined)
  }
  if (document.readyState === "complete") start()
  else window.addEventListener("load", start, { once: true })
}

/**
 * Стереть офлайн-кеш и ДОЖДАТЬСЯ этого.
 *
 * Ожидание не формальность: выход отпускает человека на экран входа и гасит токены,
 * и если стирание останется висеть в воздухе, вкладку успеют закрыть — а кеш с именами
 * клиентов останется. Ждём не дольше полутора секунд: выход не должен упираться в кеш,
 * а неубранный кеш всё равно уйдёт при следующем запуске (работник стирает лишние имена
 * на активации).
 */
export async function wipeOfflineCache(): Promise<void> {
  // Прямой путь: страница сама умеет стирать кеши, и это надёжнее переписки с работником.
  if (typeof caches !== "undefined") {
    try {
      const names = await caches.keys()
      await Promise.all(
        names.filter((name) => name.startsWith("plnr-cabinet-")).map((name) => caches.delete(name)),
      )
    } catch {
      // Приватное окно и заблокированное хранилище: стирать нечего.
    }
  }
  if (!workerAvailable()) return
  const worker = navigator.serviceWorker.controller
  if (worker === null) return
  // Сообщение работнику — второй рубеж: он может держать открытый ответ в работе
  // и положить его в кеш уже после того, как страница кеши удалила.
  await new Promise<void>((resolve) => {
    const channel = new MessageChannel()
    const done = (): void => {
      resolve()
    }
    channel.port1.onmessage = done
    const timer = setTimeout(done, 1500)
    channel.port1.addEventListener("message", () => clearTimeout(timer), { once: true })
    try {
      worker.postMessage({ type: "plnr-wipe-offline" }, [channel.port2])
    } catch {
      clearTimeout(timer)
      resolve()
    }
  })
}
